import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { DocumentVisualStore } from '../stores';
import * as _ from 'lodash';
import { PageNumberInput } from '.';
import { ResetZoomIcon, MinusIcon, PlusIcon } from '../../common/icons';
import { ClickableIcon } from '../../common/components';
import { getIconSize } from '../config/iconSizes';

type Props = {
    documentVisualStore: DocumentVisualStore;
    paginationPosition: string;
    hasLeftContainer?: boolean;
    usePageNumberInput?: boolean;
    packageId?: string;
    sessionId?: string;
    defaultScale?: number;
    minScale?: number;
    maxScale?: number;
    targetWindow: Window;
    lightMode?: boolean;
    workspacePagination?: boolean;
    minimapId?: string;
};

export const DocumentPagination: React.FC<Props> = ({
    documentVisualStore,
    paginationPosition,
    hasLeftContainer = true,
    usePageNumberInput,
    packageId,
    sessionId,
    defaultScale = 1,
    minScale = 0.5,
    maxScale = 5,
    targetWindow,
    lightMode,
    workspacePagination,
    minimapId
}) => {
    const lastLeftPosition = React.useRef<string>();

    const changeScale = (scale: number) => {
        documentVisualStore.setScale(_.clamp(+scale.toFixed(2), minScale, maxScale));
    };

    const getLeftPosition = () => {
        const minimapContainerChild = minimapId ? targetWindow.document.getElementById(minimapId) : undefined;
        const minimapContainer =
            minimapContainerChild?.parentElement?.parentElement ??
            targetWindow.document.getElementsByClassName('alpha-document-minimap')[0];

        if (minimapContainer) {
            const containerRect = minimapContainer.getBoundingClientRect();
            if (containerRect.width === 0 && containerRect.height === 0 && lastLeftPosition.current) {
                return lastLeftPosition.current;
            }

            const contentCenter = hasLeftContainer
                ? containerRect.left + containerRect.width / 2 - 50
                : containerRect.left + containerRect.width / 2;
            lastLeftPosition.current = `${contentCenter}px`;
            return lastLeftPosition.current;
        }

        return hasLeftContainer ? 'calc(50% - 50px)' : '50%';
    };

    const pageInfo = () => {
        return (
            <Observer>
                {() => (
                    <span className="page-info">
                        Page {documentVisualStore.currentPage + 1} / {documentVisualStore.totalPages}
                    </span>
                )}
            </Observer>
        );
    };

    const iconColor = lightMode ? 'black' : 'white';
    const zoomOutDisabled = documentVisualStore.scale <= minScale;
    const zoomInDisabled = documentVisualStore.scale >= maxScale;
    const iconSize = getIconSize('paginationControls', lightMode);

    const getIconStyle = (disabled?: boolean) => ({
        width: iconSize,
        height: iconSize,
        opacity: disabled ? 0.3 : 1
    });

    if (documentVisualStore.totalPages === 0) {
        return null;
    }

    return (
        <div
            className={`alpha-pagination-container ${lightMode ? 'light-mode' : ''} ${workspacePagination ? 'workspace-pagination' : ''}`}
            style={
                workspacePagination
                    ? undefined
                    : {
                          position: 'fixed',
                          left: getLeftPosition(),
                          transform: 'translateX(-50%)',
                          width: 'auto',
                          textAlign: 'left',
                          bottom: paginationPosition,
                          zIndex: 1000
                      }
            }
        >
            <div className="alpha-pagination">
                <div className="alpha-pagination-left-container" style={{ textAlign: 'left' }}>
                    {usePageNumberInput ? (
                        <PageNumberInput
                            documentVisualStore={documentVisualStore}
                            packageId={packageId}
                            sessionId={sessionId}
                            targetWindow={targetWindow}
                            lightMode={lightMode}
                        />
                    ) : (
                        pageInfo()
                    )}
                </div>
                <div className="alpha-pagination-right-container">
                    <Observer>
                        {() => (
                            <>
                                <ClickableIcon
                                    icon={MinusIcon}
                                    color={iconColor}
                                    disabled={zoomOutDisabled}
                                    style={getIconStyle(zoomOutDisabled)}
                                    onClick={() => changeScale(documentVisualStore.scale - 0.3)}
                                />

                                <ClickableIcon
                                    icon={ResetZoomIcon}
                                    color={iconColor}
                                    style={getIconStyle()}
                                    onClick={() => changeScale(defaultScale)}
                                />

                                <ClickableIcon
                                    icon={PlusIcon}
                                    color={iconColor}
                                    disabled={zoomInDisabled}
                                    style={getIconStyle(zoomInDisabled)}
                                    onClick={() => changeScale(documentVisualStore.scale + 0.3)}
                                />
                            </>
                        )}
                    </Observer>
                </div>
            </div>
        </div>
    );
};

export default observer(DocumentPagination);
